/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { HeatmapChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  VisualMapComponent,
  DataZoomComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import {
  fetchRiskHeatmapData,
  RiskHeatmapData
} from 'services/hvarisk.service';
import { useAppContext } from 'providers/AppProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFireFlameCurved } from '@fortawesome/free-solid-svg-icons';

echarts.use([
  HeatmapChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  VisualMapComponent,
  DataZoomComponent,
  CanvasRenderer
]);

interface RiskCategoryHeatmapProps {
  dateRange?: {
    datefrom: string;
    dateto: string;
  };
  campaignId?: number;
  categoryId?: number;
  serviceId?: number;
}

const RiskCategoryHeatmap: React.FC<RiskCategoryHeatmapProps> = ({
  dateRange,
  campaignId,
  categoryId,
  serviceId
}) => {
  const {
    config: { isDark },
    getThemeColor
  } = useAppContext();
  const [heatmapData, setHeatmapData] = useState<{
    elements: string[];
    campaigns: string[];
    data: any[];
    rawData: RiskHeatmapData[];
  }>({
    elements: [],
    campaigns: [],
    data: [],
    rawData: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [maxValue, setMaxValue] = useState(100);

  useEffect(() => {
    loadHeatmapData();
  }, [dateRange, campaignId, categoryId, serviceId]);

  const loadHeatmapData = async () => {
    try {
      setIsLoading(true);

      const params = {
        datefrom:
          dateRange?.datefrom ||
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split('T')[0],
        dateto: dateRange?.dateto || new Date().toISOString().split('T')[0],
        campaignid: campaignId,
        categoryid: categoryId,
        serviceid: serviceId
      };

      const data = await fetchRiskHeatmapData(params);

      // Extraer elementos y campañas únicos
      const elements = [...new Set(data.map(item => item.elementName))].sort();
      const campaigns = [
        ...new Set(data.map(item => item.campaignName))
      ].sort();

      // Encontrar el valor máximo para la escala
      const max = Math.max(...data.map(item => item.riskCount), 1);
      setMaxValue(max);

      // Transformar datos para el heatmap
      const heatmapArray = data.map(item => {
        const campaignIndex = campaigns.indexOf(item.campaignName);
        const elementIndex = elements.indexOf(item.elementName);
        return [campaignIndex, elementIndex, item.riskCount, item];
      });

      setHeatmapData({
        elements,
        campaigns,
        data: heatmapArray,
        rawData: data
      });
    } catch (error) {
      console.error('Error loading heatmap data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const option = {
    tooltip: {
      // ... configuración igual ...
      formatter: function (params: any) {
        if (!params.data || params.data.length < 4) return '';

        const rawData = params.data[3] as RiskHeatmapData;
        const severityText =
          rawData.avgSeverity >= 4.5
            ? 'Critical'
            : rawData.avgSeverity >= 3.5
            ? 'High'
            : 'Medium';

        return `
          <div>
            <div style="font-weight: bold; margin-bottom: 6px; font-size: 14px;">
              ${rawData.campaignName}
            </div>
            <div style="color: ${
              !isDark ? '#666' : '#aaa'
            }; margin-bottom: 4px; font-size: 13px;">
              ${rawData.elementName}
            </div>
            <div style="color: ${
              !isDark ? '#888' : '#999'
            }; margin-bottom: 10px; font-size: 11px;">
              Category: ${rawData.categoryName}
            </div>
            <div style="display: flex; justify-content: space-between; gap: 30px; margin-bottom: 4px;">
              <span>Total Detected:</span>
              <span style="font-weight: bold;">${rawData.riskCount}</span>
            </div>
            <div style="display: flex; justify-content: space-between; gap: 30px; margin-bottom: 4px;">
              <span>Critical (5):</span>
              <span style="font-weight: bold; color: #dc3545;">${
                rawData.criticalCount
              }</span>
            </div>
            <div style="display: flex; justify-content: space-between; gap: 30px; margin-bottom: 4px;">
              <span>High (4):</span>
              <span style="font-weight: bold; color: #fd7e14;">${
                rawData.highCount
              }</span>
            </div>
            <div style="display: flex; justify-content: space-between; gap: 30px;">
              <span>Avg. Severity:</span>
              <span style="font-weight: bold; color: ${
                rawData.avgSeverity >= 4.5
                  ? '#dc3545'
                  : rawData.avgSeverity >= 3.5
                  ? '#fd7e14'
                  : '#ffc107'
              }">
                ${rawData.avgSeverity.toFixed(1)} (${severityText})
              </span>
            </div>
          </div>
        `;
      }
    },
    grid: {
      left: '5%',
      right: '5%',
      bottom: '20%',
      top: '5%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: heatmapData.campaigns,
      splitArea: {
        show: true
      },
      axisLabel: {
        rotate: heatmapData.campaigns.length > 8 ? 45 : 0,
        interval: 0,
        fontSize: 11,
        color: !isDark ? '#666' : '#aaa'
      },
      axisLine: {
        lineStyle: {
          color: !isDark ? '#e0e0e0' : '#444'
        }
      }
    },
    yAxis: {
      type: 'category',
      data: heatmapData.elements,
      splitArea: {
        show: true
      },
      axisLabel: {
        fontSize: 11,
        color: !isDark ? '#666' : '#aaa',
        formatter: (value: string) => {
          // Truncar nombres largos
          return value.length > 37 ? value.substring(0, 37) + '...' : value;
        }
      },
      axisLine: {
        lineStyle: {
          color: !isDark ? '#e0e0e0' : '#444'
        }
      }
    },
    visualMap: {
      min: 0,
      max: maxValue,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: '2%',
      inRange: {
        color: [
          '#f7fbff', // Almost white (no risks)
          '#aef7a7', // Very light blue
          '#9ee18a', // Light blue
          '#6bd65b', // Medium blue
          '#fd8d3c', // Orange
          '#e6550d', // Dark orange
          '#a63603', // Dark red
          '#7f2704' // Very dark red
        ]
      },
      text: ['High Risk', 'Low Risk'],
      textStyle: {
        color: !isDark ? '#666' : '#aaa',
        fontSize: 11
      }
    },
    series: [
      {
        name: 'Risk Count',
        type: 'heatmap',
        data: heatmapData.data,
        label: {
          show: true,
          fontSize: 10,
          color: (params: any) => {
            // Texto blanco para valores altos, negro para bajos
            return params.data[2] > maxValue * 0.6 ? '#fff' : '#333';
          },
          formatter: (params: any) => {
            return params.data[2] || '';
          }
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            borderColor: getThemeColor('primary'),
            borderWidth: 2
          }
        }
      }
    ],
    dataZoom: [
      {
        type: 'slider',
        show: heatmapData.campaigns.length > 10,
        xAxisIndex: [0],
        start: 0,
        end: 100,
        height: 20,
        bottom: 65,
        backgroundColor: !isDark ? '#f4f4f4' : '#333',
        fillerColor: !isDark
          ? 'rgba(70, 130, 180, 0.4)'
          : 'rgba(70, 130, 180, 0.6)',
        borderColor: !isDark ? '#ddd' : '#555'
      }
    ]
  };

  if (isLoading) {
    return (
      <Card className="h-100">
        <Card.Body
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '450px' }}
        >
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (heatmapData.data.length === 0) {
    return (
      <Card className="h-100">
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={faFireFlameCurved}
              className="me-2 text-primary"
            />
            Risk Distribution Heatmap
          </h5>
        </Card.Header>
        <Card.Body
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ minHeight: '450px' }}
        >
          <FontAwesomeIcon
            icon={faFireFlameCurved}
            size="3x"
            className="text-muted opacity-25 mb-3"
          />
          <p className="text-muted text-center">
            No detected risks found for the selected filters
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FontAwesomeIcon
              icon={faFireFlameCurved}
              className="me-2 text-primary"
            />
            Risk Elements Distribution Heatmap
          </h5>
          <small className="text-muted">
            {heatmapData.campaigns.length} campaigns ×{' '}
            {heatmapData.elements.length} elements
          </small>
        </div>
      </Card.Header>
      <Card.Body>
        <ReactEChartsCore
          echarts={echarts}
          option={option}
          style={{ height: '550px' }}
          notMerge={true}
          lazyUpdate={true}
        />
      </Card.Body>
    </Card>
  );
};

export default RiskCategoryHeatmap;
